// Pages of an appended original (a 履歴書, a 職務経歴書) shown after the
// report exactly as the output embeds them, with the reviewer's redactions:
// drag to paint a white box over an address or e-mail cell, cut the band
// a box spans so the rows below close up, remove a text row of a PDF with
// the × beside it (a cut band of exactly that row), remove a whole page,
// restore it. Boxes are stored as fractions of the page so the same
// numbers drive the output render; the page is drawn with its cut bands
// taken out, the way the output has them.

import { useRef, useState } from "react";
import type { CSSProperties, PointerEvent } from "react";
import { boxTextLayout, cutBands, type RedactionBox, type SourcePageInfo, type SourceRedactions, type SourceTextDoc, type SourceTextEdits } from "@agp/shared";
import { Editable } from "@/components/preview/Editable";

export interface AppendixSource {
  id: string;
  filename: string;
  /** Null while the server is still rendering the pages. */
  pages: SourcePageInfo[] | null;
  redactions: SourceRedactions;
  /** Present when the reviewer may redact; absent previews are read-only. */
  onChange?: (redactions: SourceRedactions) => void;
  /** Word originals can also be shown as editable text. */
  view?: "pages" | "text";
  text?: {
    /** Null while loading. */
    doc: SourceTextDoc | null;
    textEdits: SourceTextEdits;
    onChange?: (edits: SourceTextEdits) => void;
  };
}

export interface PageGeometry {
  pageW: number;
  pageH: number;
  marginLeft: number;
  marginTop: number;
  contentW: number;
  capacity: number;
}

const RASTER_DPI = 150;
const MIN_BOX = 0.004;

export const REMOVED_BAR_HEIGHT = 44;

export function RemovedPageBar({
  source,
  pageIndex,
  geometry,
  onRestore,
}: {
  source: AppendixSource;
  pageIndex: number;
  geometry: PageGeometry;
  onRestore?: () => void;
}) {
  return (
    <div
      data-removed-page={`${source.id}:${pageIndex}`}
      style={{
        width: geometry.pageW,
        height: REMOVED_BAR_HEIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 16px",
        background: "#f3f4f6",
        border: "1px dashed #c4c8cf",
        color: "#6b7280",
        fontFamily: "system-ui, sans-serif",
        fontSize: 12,
        boxSizing: "border-box",
      }}
    >
      <span>
        Page {pageIndex + 1} of {source.filename} is left out of the output
      </span>
      {onRestore && (
        <button type="button" onClick={onRestore} style={restoreButtonStyle}>
          Restore page
        </button>
      )}
    </div>
  );
}

const smallButton: CSSProperties = {
  fontFamily: "system-ui, sans-serif",
  fontSize: 10,
  lineHeight: "16px",
  padding: "0 6px",
  border: "1px solid #2563eb",
  borderRadius: 3,
  background: "#fff",
  color: "#2563eb",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const restoreButtonStyle: CSSProperties = {
  fontFamily: "system-ui, sans-serif",
  fontSize: 12,
  padding: "4px 10px",
  border: "1px solid #c4c8cf",
  borderRadius: 4,
  background: "#fff",
  cursor: "pointer",
};

export function AppendixPage({
  source,
  page,
  geometry,
  chrome,
  label,
  onRemovePage,
}: {
  source: AppendixSource;
  page: SourcePageInfo;
  geometry: PageGeometry;
  chrome: React.ReactNode;
  label: string;
  onRemovePage?: () => void;
}) {
  const editing = source.onChange != null;
  const imgW = (page.width / RASTER_DPI) * 96;
  const imgH = (page.height / RASTER_DPI) * 96;
  const scale = Math.min(geometry.contentW / imgW, geometry.capacity / imgH, 1);
  const w = imgW * scale;
  const h = imgH * scale;
  // Cut bands shorten the page: positions on screen are page positions
  // less the bands above them, and pointer positions map back the same way.
  const bands = cutBands(source.redactions.boxes, page.index);
  const kept = 1 - bands.reduce((n, b) => n + b.h, 0);
  const toScreen = (y: number) => y - bands.reduce((n, b) => n + Math.max(0, Math.min(y, b.y + b.h) - b.y), 0);
  const toPage = (screenY: number) => {
    let y = screenY;
    for (const b of bands) if (y >= b.y) y += b.h;
    return Math.min(1, y);
  };
  const segments: Array<{ y: number; h: number }> = [];
  let at = 0;
  for (const b of bands) {
    if (b.y > at) segments.push({ y: at, h: b.y - at });
    at = Math.max(at, b.y + b.h);
  }
  if (at < 1) segments.push({ y: at, h: 1 - at });
  const shownH = h * kept;
  const overlayRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState<RedactionBox | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [hoverLine, setHoverLine] = useState<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);

  const fraction = (e: PointerEvent) => {
    const rect = overlayRef.current!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      y: toPage(Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)) * kept),
    };
  };
  const lines = editing ? (page.lines ?? []) : [];
  const sameBand = (a: { y: number; h: number }, b: { y: number; h: number }) => Math.abs(a.y - b.y) < 1e-6 && Math.abs(a.h - b.h) < 1e-6;
  const isLineCut = (b: RedactionBox) => b.cut === true && b.x === 0 && b.w === 1 && lines.some((l) => sameBand(l, b));
  const trackLine = (e: PointerEvent) => {
    if (lines.length === 0 || start.current) return;
    const rect = overlayRef.current!.getBoundingClientRect();
    const at = (e.clientY - rect.top) / rect.height;
    const y = at < 0 || at > 1 ? -1 : toPage(at * kept);
    const index = lines.findIndex((l) => y >= l.y && y < l.y + l.h && toScreen(l.y + l.h) - toScreen(l.y) > 1e-6);
    setHoverLine(index >= 0 ? index : null);
  };
  const cutLine = (index: number) => {
    const l = lines[index]!;
    source.onChange?.({ ...source.redactions, boxes: [...source.redactions.boxes, { page: page.index, x: 0, y: l.y, w: 1, h: l.h, cut: true }] });
    setHoverLine(null);
  };
  /** A page box on the shortened page, as CSS percentages of it. */
  const screenBox = (b: { y: number; h: number }) => ({
    top: `${(toScreen(b.y) / kept) * 100}%`,
    height: `${((toScreen(b.y + b.h) - toScreen(b.y)) / kept) * 100}%`,
  });
  const boxFrom = (a: { x: number; y: number }, b: { x: number; y: number }): RedactionBox => ({
    page: page.index,
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(a.x - b.x),
    h: Math.abs(a.y - b.y),
  });

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (!editing || e.button !== 0) return;
    if ((e.target as HTMLElement).closest("[data-redaction-box]")) return;
    e.preventDefault();
    overlayRef.current?.setPointerCapture(e.pointerId);
    start.current = fraction(e);
    setSelected(null);
    setHoverLine(null);
    setDraft(boxFrom(start.current, start.current));
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!start.current) return;
    setDraft(boxFrom(start.current, fraction(e)));
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (!start.current) return;
    const box = boxFrom(start.current, fraction(e));
    start.current = null;
    setDraft(null);
    if (box.w >= MIN_BOX && box.h >= MIN_BOX) {
      source.onChange?.({ ...source.redactions, boxes: [...source.redactions.boxes, box] });
    }
  };

  const boxes = source.redactions.boxes
    .map((b, i) => ({ b, i }))
    .filter(({ b }) => b.page === page.index);
  const removeBox = (i: number) => {
    source.onChange?.({ ...source.redactions, boxes: source.redactions.boxes.filter((_, k) => k !== i) });
    setSelected(null);
  };
  const setBoxText = (i: number, text: string) => {
    source.onChange?.({
      ...source.redactions,
      boxes: source.redactions.boxes.map((b, k) => (k === i ? { ...b, ...(text.trim() ? { text } : { text: undefined }) } : b)),
    });
  };
  const setBoxCut = (i: number, cut: boolean) => {
    source.onChange?.({
      ...source.redactions,
      boxes: source.redactions.boxes.map((b, k) => (k === i ? { ...b, ...(cut ? { cut: true, text: undefined } : { cut: undefined }) } : b)),
    });
    setSelected(null);
  };

  return (
    <div
      data-appendix-page={`${source.id}:${page.index}`}
      style={{
        width: geometry.pageW,
        height: geometry.pageH,
        background: "#ffffff",
        position: "relative",
        boxShadow: "0 1px 6px rgba(0,0,0,0.18)",
        overflow: "hidden",
      }}
    >
      {chrome}
      <div
        style={{ position: "absolute", top: geometry.marginTop, left: geometry.marginLeft, width: w, height: shownH }}
        onPointerMove={trackLine}
        onPointerLeave={() => setHoverLine(null)}
      >
        {segments.map((seg) => (
          <div key={seg.y} style={{ width: w, height: h * seg.h, overflow: "hidden" }}>
            <img src={page.url} alt="" draggable={false} style={{ width: w, height: h, display: "block", userSelect: "none", marginTop: -h * seg.y }} />
          </div>
        ))}
        <div
          ref={overlayRef}
          data-redaction-overlay=""
          title={editing ? "Drag to paint a white box over anything that must not appear in the output" : undefined}
          style={{ position: "absolute", inset: 0, cursor: editing ? "crosshair" : "default", touchAction: "none" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={() => {
            start.current = null;
            setDraft(null);
          }}
        >
          {boxes.map(({ b, i }) => {
            if (b.cut) {
              // A cut band is a seam: the rows above and below now touch.
              // Neighbouring rows cut one by one share a seam, so their
              // undo buttons sit side by side.
              const stacked = boxes.filter((o) => o.i < i && o.b.cut && Math.abs(toScreen(o.b.y) - toScreen(b.y)) < 1e-6).length;
              return (
                <div
                  key={i}
                  data-redaction-cut={i}
                  onPointerDown={(e) => e.stopPropagation()}
                  style={{ position: "absolute", left: 0, right: 0, top: `calc(${screenBox(b).top} - 1px)`, height: 0, borderTop: editing ? `2px dashed ${stacked === 0 ? "rgba(220,38,38,0.7)" : "transparent"}` : undefined, zIndex: 1 }}
                >
                  {editing && (
                    <button
                      type="button"
                      data-undo-cut=""
                      title="Put the cut rows back"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isLineCut(b)) removeBox(i);
                        else setBoxCut(i, false);
                      }}
                      style={{ ...smallButton, position: "absolute", right: 4 + stacked * 92, top: -9 }}
                    >
                      rows cut · undo
                    </button>
                  )}
                </div>
              );
            }
            // Box size in page pixels, so the text layout matches the SVG
            // the output draws at its own pixel size.
            const bw = b.w * w;
            const bh = b.h * h;
            const layout = boxTextLayout(b.text ?? "", bw, bh);
            return (
            <div
              key={i}
              data-redaction-box={i}
              tabIndex={editing ? 0 : -1}
              onClick={(e) => {
                e.stopPropagation();
                if (editing) setSelected(i);
              }}
              onPointerDown={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                left: `${b.x * 100}%`,
                width: `${b.w * 100}%`,
                ...screenBox(b),
                background: "#ffffff",
                outline: editing ? `1px ${selected === i ? "solid" : "dashed"} ${selected === i ? "#2563eb" : "rgba(220,38,38,0.7)"}` : undefined,
                cursor: editing ? "text" : "default",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: layout.padX,
                  top: layout.padY,
                  right: layout.padX,
                  bottom: layout.padY,
                  fontSize: layout.fontPx,
                  lineHeight: `${layout.lineHeight}px`,
                  fontFamily: "'Noto Sans CJK JP', 'Hiragino Sans', 'Yu Gothic', sans-serif",
                  color: "#000",
                }}
              >
                {editing ? (
                  <Editable
                    id={`box.${source.id}.${page.index}.${i}`}
                    value={b.text ?? ""}
                    multiline
                    placeholder={selected === i ? "Type replacement text" : undefined}
                    style={{ minHeight: "100%", lineHeight: "inherit" }}
                    onCommit={(t) => setBoxText(i, t)}
                  />
                ) : (
                  b.text
                )}
              </div>
              {editing && selected === i && (
                <>
                  <button
                    type="button"
                    aria-label="Remove box"
                    data-remove-box=""
                    onClick={(e) => {
                      e.stopPropagation();
                      removeBox(i);
                    }}
                    style={{
                      position: "absolute",
                      top: -10,
                      right: -10,
                      width: 20,
                      height: 20,
                      borderRadius: 10,
                      border: "1px solid #2563eb",
                      background: "#fff",
                      color: "#2563eb",
                      fontSize: 12,
                      lineHeight: "18px",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    ×
                  </button>
                  <button
                    type="button"
                    data-cut-box=""
                    title="Take these rows out and close the gap: the page below moves up"
                    onClick={(e) => {
                      e.stopPropagation();
                      setBoxCut(i, true);
                    }}
                    style={{ ...smallButton, position: "absolute", left: 2, bottom: 2 }}
                  >
                    Cut rows, close the gap
                  </button>
                </>
              )}
            </div>
            );
          })}
          {hoverLine != null && (
            <div style={{ position: "absolute", left: 0, right: 0, ...screenBox(lines[hoverLine]!), background: "rgba(220,38,38,0.1)", pointerEvents: "none" }} />
          )}
          {draft && (
            <div
              style={{
                position: "absolute",
                left: `${draft.x * 100}%`,
                width: `${draft.w * 100}%`,
                ...screenBox(draft),
                background: "rgba(255,255,255,0.85)",
                outline: "1px dashed #2563eb",
                pointerEvents: "none",
              }}
            />
          )}
        </div>
        {lines.length > 0 && (
          <div data-line-gutter="" style={{ position: "absolute", right: "100%", top: 0, bottom: 0, width: Math.min(28, geometry.marginLeft) }}>
            {hoverLine != null && (
              <button
                type="button"
                data-remove-line={hoverLine}
                title="Remove this row: the page below moves up"
                onClick={() => cutLine(hoverLine)}
                style={{ ...smallButton, position: "absolute", right: 4, top: `calc(${screenBox(lines[hoverLine]!).top} + ${screenBox(lines[hoverLine]!).height} - 17px)`, borderColor: "#dc2626", color: "#dc2626" }}
              >
                ×
              </button>
            )}
          </div>
        )}
      </div>
      {editing && onRemovePage && (
        <button
          type="button"
          data-remove-page=""
          onClick={onRemovePage}
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            fontFamily: "system-ui, sans-serif",
            fontSize: 11,
            padding: "3px 8px",
            border: "1px solid #c4c8cf",
            borderRadius: 4,
            background: "rgba(255,255,255,0.95)",
            color: "#374151",
            cursor: "pointer",
          }}
        >
          Remove page
        </button>
      )}
      <span
        style={{
          position: "absolute",
          right: 10,
          bottom: 6,
          fontFamily: "system-ui, sans-serif",
          fontSize: 9,
          color: "#B0B3B8",
        }}
      >
        {label}
      </span>
    </div>
  );
}

export function LoadingAppendixPage({ source, geometry }: { source: AppendixSource; geometry: PageGeometry }) {
  return (
    <div
      style={{
        width: geometry.pageW,
        height: geometry.pageH,
        background: "#ffffff",
        position: "relative",
        boxShadow: "0 1px 6px rgba(0,0,0,0.18)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#9aa0a6",
        fontFamily: "system-ui, sans-serif",
        fontSize: 13,
      }}
    >
      Rendering the pages of {source.filename}…
    </div>
  );
}
